import React, { useEffect, useState } from 'react';
import { BaseDialog, PasswordInput, ProcessHandler } from '@src/components';
import { Grid, Typography, TextField, Button } from '@mui/material';
import { StyledCreateProduct } from '@src/containers/Product/index.style';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { PERMISSIONS, ROLE_LABEL, ROLE_VALUE } from '@src/constants';
import { useDispatch } from 'react-redux';
import apis from '@src/apis';
import actions from '@src/redux/actions';
import SelectPermission from '../SelectPermission';

const initialAccount = {
  username: '',
  password: '',
  recheckPassword: '',
  roles: ROLE_VALUE.EMPLOYEE,
  permissions: [],
};

const validationSchema = yup.object({
  username: yup.string().required('Vui lòng nhập tên người dùng'),
  password: yup
    .string()
    .required('Vui lòng nhập mật khẩu')
    .min(6, 'Mật khẩu phải có ít nhất 6 ký tự'),
  recheckPassword: yup
    .string()
    .oneOf([yup.ref('password'), null], 'Mật khẩu nhập lại không khớp')
    .required('Vui lòng nhập lại mật khẩu'),
  roles: yup.string().required('Vui lòng chọn chức vụ'),
  permissions: yup
    .array()
    .of(yup.string().oneOf(Object.keys(PERMISSIONS)))
    .min(1, 'Phải chọn ít nhất một quyền'),
});

const CreateAccountDialog = ({
  account,
  open,
  onClose,
  onCreateNewAccount,
  onUpdateProduct,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  const handleSubmitForm = async (values, helpers) => {
    const processData = {
      ...values,
      roles: [values.roles],
    };

    let execFunc = apis.account.createAccount;

    if (account) {
      execFunc = apis.account.updateAccount;
      processData.accountId = account.id;
    }

    setIsLoading(true);
    const res = await execFunc(processData);
    setIsLoading(false);

    if (!res || !res.status) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          code: res?.code || 0,
        }),
      );
      return;
    }

    dispatch(
      actions.noti.push({
        severity: 'success',
        message: account
          ? 'Cập nhật tài khoản thành công'
          : 'Tạo tài khoản thành công',
      }),
    );

    onCreateNewAccount?.(res.result);

    if (account) {
      onUpdateProduct?.(res.result);
      return;
    }

    helpers.resetForm(initialAccount);
  };

  const formik = useFormik({
    initialValues: initialAccount,
    validationSchema,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: handleSubmitForm,
  });

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      formik.handleSubmit();
    }
  };

  const handleUpdateForm = ({ name, value }) => {
    formik.setValues({ ...formik.values, [name]: value });
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  useEffect(() => {
    if (!open || !account) return;
    formik.setValues({
      username: account.username || '',
      roles: account.roles?.[0] || '',
      permissions: account?.permissions || [],
      password: 'fake-password',
      recheckPassword: 'fake-password',
    });
  }, [account, open]);

  return (
    <BaseDialog
      title={account ? 'Sửa tài khoản' : 'Thêm mới tải khoản'}
      open={open}
      onClose={handleClose}
    >
      <form onSubmit={formik.handleSubmit}>
        <StyledCreateProduct>
          <Grid container spacing={4}>
            <Grid item xs={6}>
              <div className="control-item">
                <Typography>
                  Tên tài khoản <span className="req">*</span>
                </Typography>
                <TextField
                  size="small"
                  placeholder="Nhập tên tài khoản"
                  name="username"
                  id="username"
                  onKeyDown={handleKeyDown}
                  value={formik.values.username}
                  onChange={formik.handleChange}
                  error={Boolean(formik.errors.username)}
                  helperText={formik.touched.username && formik.errors.username}
                />
              </div>
              <div className="control-item">
                <Typography>
                  Chọn chức vụ <span className="req">*</span>
                </Typography>
                <TextField
                  size="small"
                  placeholder="Chọn chức vụ"
                  name="roles"
                  id="roles"
                  value={ROLE_LABEL[formik.values.roles]}
                />
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className="control-item">
                <Typography>
                  Nhập mật khẩu <span className="req">*</span>
                </Typography>
                <PasswordInput
                  size="small"
                  placeholder="Nhập mật khẩu"
                  name="password"
                  id="password"
                  onKeyDown={handleKeyDown}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  disabled={!!account}
                  error={Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                />
              </div>
              <div className="control-item">
                <Typography>
                  Nhập lại mật khẩu <span className="req">*</span>
                </Typography>
                <PasswordInput
                  size="small"
                  placeholder="Nhập lại mật khẩu"
                  name="recheckPassword"
                  id="recheckPassword"
                  disabled={!!account}
                  onKeyDown={handleKeyDown}
                  value={formik.values.recheckPassword}
                  onChange={formik.handleChange}
                  error={Boolean(formik.errors.recheckPassword)}
                  helperText={
                    formik.touched.recheckPassword &&
                    formik.errors.recheckPassword
                  }
                />
              </div>
            </Grid>
            <Grid item xs={12} className="description">
              <div className="control-item">
                <Typography>
                  Chọn quyền hạn cho tài khoản <span className="req">*</span>
                </Typography>
                <SelectPermission
                  typeHelperText="error"
                  currentPermissions={formik.values.permissions}
                  helperText={
                    formik.touched.permissions && formik.errors.permissions
                  }
                  onChange={(newPermissions) => {
                    const processPermissionsData = newPermissions.map(
                      (item) => item?.value,
                    );
                    handleUpdateForm({
                      name: 'permissions',
                      value: processPermissionsData,
                    });
                  }}
                />
              </div>
            </Grid>
          </Grid>
          <div className="button-group">
            <Button variant="contained" type="submit">
              <ProcessHandler loading={isLoading}>
                {account ? 'Cập nhật' : 'Tạo'}
              </ProcessHandler>
            </Button>
            <Button variant="outlined" onClick={handleClose}>
              Hủy
            </Button>
          </div>
        </StyledCreateProduct>
      </form>
    </BaseDialog>
  );
};

export default CreateAccountDialog;
